import {
  parseLoomAuthoritativeRebuildRequestV5,
  type LoomAircraftOccurrenceTransitionRebuildDefinition,
} from "../../api/loomRebuildV5";
import {
  parseLoomAuthoritativeRebuildRequestV6,
  type LoomAircraftOccurrenceTransitionRebuildDefinitionV3,
} from "../../api/loomRebuildV6";
import { canonicalLoomDefinitionJson } from "./loomAcceptedDefinitionBinding";
import type { LoomOwnedDisplaySurface } from "./loomViewportPresentation";

export const LOOM_TRANSITION_ASSET_SCHEMA = "loom.native-aircraft-occurrence-surface/v2" as const;
export const LOOM_TRANSITION_ACCEPTED_SCHEMA = "loom.accepted-aircraft-occurrence-surface/v2" as const;
export const LOOM_TRANSITION_UNION_ALGORITHM = "loom.implicit-guided-explicit-transition-union/v1" as const;
export const LOOM_TRANSITION_SOURCE_AUTHORITY = "ephemeral-local-fixture-self-attested/v1" as const;
export const LOOM_TRANSITION_ASSET_SCHEMA_V3 = "loom.native-aircraft-occurrence-surface/v3" as const;
export const LOOM_TRANSITION_ACCEPTED_SCHEMA_V3 = "loom.accepted-aircraft-occurrence-surface/v3" as const;
export const LOOM_TRANSITION_UNION_ALGORITHM_V3 = "loom.implicit-guided-explicit-transition-union/v2" as const;
const MAGIC_V2 = [76, 79, 79, 77, 65, 79, 50, 0] as const;
const MAGIC_V3 = [76, 79, 79, 77, 65, 79, 51, 0] as const;
const PREFIX_BYTES = 16; const MAX_HEADER_BYTES = 4 * 1024 * 1024; const MAX_ASSET_BYTES = 128 * 1024 * 1024;
const UINT32_NONE = 0xffffffff; const SHA = /^sha256:[0-9a-f]{64}$/;

interface Descriptor {
  name: string; dtype: "f64-le" | "u32-le"; shape: number[];
  byteOffset: number; byteLength: number; semantic: string;
}

export interface LoomAircraftTransitionAssetHeader {
  schema: typeof LOOM_TRANSITION_ASSET_SCHEMA | typeof LOOM_TRANSITION_ASSET_SCHEMA_V3; version: 2 | 3;
  acceptedSchema: typeof LOOM_TRANSITION_ACCEPTED_SCHEMA | typeof LOOM_TRANSITION_ACCEPTED_SCHEMA_V3;
  definitionBinding: {
    definitionRevision: string; assemblyTopologyRevision: string; poseRevision: string;
    definition: LoomAircraftOccurrenceTransitionRebuildDefinition | LoomAircraftOccurrenceTransitionRebuildDefinitionV3;
  } & Record<string, unknown>;
  cacheReceipt: Record<string, unknown> & { sceneRevision: string };
  occurrences: Array<Record<string, unknown> & {
    occurrenceIndex: number; occurrenceId: string; bodyIndex: number; bodyId: string;
    unionGroupIndex: number; role: "wing" | "fuselage"; parity: "proper" | "reflected";
  }>;
  bodies: Array<Record<string, unknown> & {
    bodyIndex: number; bodyId: string; occurrenceIndex: number; occurrenceId: string;
  }>;
  transitions: Array<Record<string, unknown> & { transitionIndex: number; transitionId: string; definition: unknown; continuityReceipt?: LoomJunctionContinuityReceiptV3 }>;
  transitionBodies: Array<Record<string, unknown> & {
    transitionBodyIndex: number; transitionId: string; bodyId: string; side: "starboard" | "port";
  }>;
  unionGroups: Array<Record<string, unknown> & {
    unionGroupIndex: number; unionGroupId: string; geometryRevision: string; topologyRevision: string;
    patches: Array<Record<string, unknown> & { patchIndex: number; ownerKind: "occurrence" | "transition"; ownerIndex: number }>;
    weldedContacts: Array<Record<string, unknown> & { contactIndex: number; vertexArrayName: string }>;
  }>;
  surface: {
    representation: "adapted-parametric-explicit-transition-union" | "adapted-parametric-explicit-transition-continuity-union";
    geometryRevision: string; topologyRevision: string; vertexCount: number; faceCount: number;
    patchCount: number; unionGroupCount: number; transitionCount: number; transitionBodyCount: number;
    qc: Record<string, unknown>;
  };
  arrays: Descriptor[]; payloadSha256: string;
  capabilities: Record<string, unknown> & { verticalTailDorsalAdapter: false; junctionTransitions: true };
  [key: string]: unknown;
}

export interface LoomJunctionContinuityParentReceiptV3 {
  requestedContinuity: "g1-tangent" | "g2-curvature"; achievedContinuity: "g1-tangent" | "g2-curvature";
  maximumPositionGapM: number; maximumTangentPlaneAngleRad: number;
  maximumAbsoluteCurvatureMismatchPerM: number | null; maximumRelativeCurvatureMismatch: number | null;
  worstMetric: "g1-tangent-plane-angle" | "g2-normal-curvature"; worstStationIndex: number;
  worstJunctionAngleDeg: number; worstLocationM: [number, number, number];
}
export interface LoomJunctionContinuityReceiptV3 {
  schema: "loom.aircraft-junction-transition-continuity-receipt/v3";
  transitionRevision: string; geometryRevision: string; topologyRevision: string; receiptRevision: string;
  requestedContinuity: { parentA: "g1-tangent" | "g2-curvature"; parentB: "g1-tangent" | "g2-curvature" };
  achievedContinuity: { parentA: "g1-tangent" | "g2-curvature"; parentB: "g1-tangent" | "g2-curvature" };
  parentA: LoomJunctionContinuityParentReceiptV3; parentB: LoomJunctionContinuityParentReceiptV3;
  fairness: { curvatureVariationEnergyPerM3: number; inflectionCount: number; rippleCount: number; sampleCountPerStation: 65 };
  canonicalQcPassed: true;
}

export interface LoomVerifiedAircraftTransitionAsset {
  readonly integrity: { readonly status: "verified"; readonly payloadSha256: true };
  readonly header: LoomAircraftTransitionAssetHeader;
  readonly positions: Float64Array; readonly faces: Uint32Array;
  readonly faceOccurrenceIndices: Uint32Array; readonly faceBodyIndices: Uint32Array;
  readonly faceTransitionBodyIndices: Uint32Array; readonly facePatchIndices: Uint32Array;
  readonly displaySurface: LoomOwnedDisplaySurface;
}

type Row = Record<string, unknown>;
function object(value: unknown, label: string): Row {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error(`${label} must be an object`);
  return value as Row;
}
function exact(value: unknown, keys: readonly string[], label: string): Row {
  const row = object(value, label); const actual = Object.keys(row);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key))) throw new Error(`${label} has an unsupported field set`);
  return row;
}
function rows(value: unknown, label: string, count?: number): unknown[] {
  if (!Array.isArray(value) || (count !== undefined && value.length !== count)) throw new Error(`${label} has an invalid row count`);
  return value;
}
function integer(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) throw new Error(`${label} is invalid`);
  return value;
}
function string(value: unknown, label: string): string {
  if (typeof value !== "string" || !value.trim()) throw new Error(`${label} is invalid`);
  return value;
}
function sha(value: unknown, label: string): string {
  const result = string(value, label); if (!SHA.test(result)) throw new Error(`${label} is not a SHA-256 receipt`); return result;
}
function canonical(value: unknown): string { return canonicalLoomDefinitionJson(value); }

const CONTINUITY = ["g1-tangent", "g2-curvature"] as const;
function finite(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) throw new Error(`${label} is invalid`);
  return value;
}
function signedFinite(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`${label} is invalid`);
  return value;
}
function continuity(value: unknown, label: string): typeof CONTINUITY[number] {
  if (!CONTINUITY.includes(value as typeof CONTINUITY[number])) throw new Error(`${label} is invalid`);
  return value as typeof CONTINUITY[number];
}
function continuityPair(value: unknown, label: string) {
  const row = exact(value, ["parentA", "parentB"], label);
  return { parentA: continuity(row.parentA, `${label}.parentA`), parentB: continuity(row.parentB, `${label}.parentB`) };
}
function continuityParent(value: unknown, label: string): LoomJunctionContinuityParentReceiptV3 {
  const row = exact(value, ["requestedContinuity", "achievedContinuity", "maximumPositionGapM",
    "maximumTangentPlaneAngleRad", "maximumAbsoluteCurvatureMismatchPerM", "maximumRelativeCurvatureMismatch",
    "worstMetric", "worstStationIndex", "worstJunctionAngleDeg", "worstLocationM"], label);
  const requestedContinuity = continuity(row.requestedContinuity, `${label}.requestedContinuity`);
  const achievedContinuity = continuity(row.achievedContinuity, `${label}.achievedContinuity`);
  if (requestedContinuity !== achievedContinuity) throw new Error(`${label} silently downgraded continuity`);
  const absolute = row.maximumAbsoluteCurvatureMismatchPerM === null ? null : finite(row.maximumAbsoluteCurvatureMismatchPerM, `${label}.maximumAbsoluteCurvatureMismatchPerM`);
  const relative = row.maximumRelativeCurvatureMismatch === null ? null : finite(row.maximumRelativeCurvatureMismatch, `${label}.maximumRelativeCurvatureMismatch`);
  if ((achievedContinuity === "g1-tangent") !== (absolute === null && relative === null)) throw new Error(`${label} curvature evidence is inconsistent`);
  const worstMetric = row.worstMetric;
  if (worstMetric !== "g1-tangent-plane-angle" && worstMetric !== "g2-normal-curvature") throw new Error(`${label}.worstMetric is invalid`);
  const location = rows(row.worstLocationM, `${label}.worstLocationM`, 3).map((entry) => signedFinite(entry, `${label}.worstLocationM`));
  return { requestedContinuity, achievedContinuity, maximumPositionGapM: finite(row.maximumPositionGapM, `${label}.maximumPositionGapM`),
    maximumTangentPlaneAngleRad: finite(row.maximumTangentPlaneAngleRad, `${label}.maximumTangentPlaneAngleRad`),
    maximumAbsoluteCurvatureMismatchPerM: absolute, maximumRelativeCurvatureMismatch: relative, worstMetric,
    worstStationIndex: integer(row.worstStationIndex, `${label}.worstStationIndex`),
    worstJunctionAngleDeg: finite(row.worstJunctionAngleDeg, `${label}.worstJunctionAngleDeg`),
    worstLocationM: location as [number, number, number] };
}
export function parseLoomJunctionContinuityReceiptV3(value: unknown): LoomJunctionContinuityReceiptV3 {
  const row = exact(value, ["schema", "transitionRevision", "geometryRevision", "topologyRevision", "requestedContinuity",
    "achievedContinuity", "parentA", "parentB", "fairness", "canonicalQcPassed", "receiptRevision"], "continuityReceipt");
  if (row.schema !== "loom.aircraft-junction-transition-continuity-receipt/v3" || row.canonicalQcPassed !== true) {
    throw new Error("continuity receipt identity is unsupported");
  }
  const requestedContinuity = continuityPair(row.requestedContinuity, "continuityReceipt.requestedContinuity");
  const achievedContinuity = continuityPair(row.achievedContinuity, "continuityReceipt.achievedContinuity");
  if (canonical(requestedContinuity) !== canonical(achievedContinuity)) throw new Error("continuity receipt silently downgraded continuity");
  const parentA = continuityParent(row.parentA, "continuityReceipt.parentA");
  const parentB = continuityParent(row.parentB, "continuityReceipt.parentB");
  if (canonical(requestedContinuity) !== canonical({ parentA: parentA.requestedContinuity, parentB: parentB.requestedContinuity })) {
    throw new Error("continuity receipt parent evidence disagrees");
  }
  const fairness = exact(row.fairness, ["curvatureVariationEnergyPerM3", "inflectionCount", "rippleCount", "sampleCountPerStation"], "continuityReceipt.fairness");
  if (fairness.sampleCountPerStation !== 65) throw new Error("continuity receipt fairness sampling is unsupported");
  return { schema: row.schema, transitionRevision: sha(row.transitionRevision, "transitionRevision"),
    geometryRevision: sha(row.geometryRevision, "geometryRevision"), topologyRevision: sha(row.topologyRevision, "topologyRevision"),
    receiptRevision: sha(row.receiptRevision, "receiptRevision"), requestedContinuity, achievedContinuity, parentA, parentB,
    fairness: { curvatureVariationEnergyPerM3: finite(fairness.curvatureVariationEnergyPerM3, "curvatureVariationEnergyPerM3"),
      inflectionCount: integer(fairness.inflectionCount, "inflectionCount"), rippleCount: integer(fairness.rippleCount, "rippleCount"), sampleCountPerStation: 65 },
    canonicalQcPassed: true };
}

function definitionBinding(value: unknown): LoomAircraftTransitionAssetHeader["definitionBinding"] {
  const item = exact(value, ["definitionSchema", "definitionRevision", "evaluatorId", "topologyId",
    "unionAlgorithmId", "assemblyTopologyRevision", "poseRevision", "definition"], "definitionBinding");
  const v3 = item.definitionSchema === "loom.aircraft-occurrence-rebuild-definition/v3";
  if (v3 ? item.evaluatorId !== "loom.native-aircraft-occurrence-transition-union/v2"
      || item.topologyId !== "ordered-aircraft-occurrence-transition-continuity-union"
      || item.unionAlgorithmId !== LOOM_TRANSITION_UNION_ALGORITHM_V3
    : item.definitionSchema !== "loom.aircraft-occurrence-rebuild-definition/v2"
      || item.evaluatorId !== "loom.native-aircraft-occurrence-transition-union/v1"
      || item.topologyId !== "ordered-aircraft-occurrence-transition-union"
      || item.unionAlgorithmId !== LOOM_TRANSITION_UNION_ALGORITHM) throw new Error("transition definition markers are unsupported");
  const sourceDefinition = { documentId: "asset-definition", revision: 0, stateId: "loom-state-0000000000000000" };
  const parsed = v3 ? parseLoomAuthoritativeRebuildRequestV6({
    schema: "loom.authoritative-rebuild-request/v6", requestId: "asset-definition-binding", sourceDefinition,
    evaluatorId: "loom.native-aircraft-occurrence-transition-union/v2",
    topology: "ordered-aircraft-occurrence-transition-continuity-union", definition: item.definition,
  }).definition : parseLoomAuthoritativeRebuildRequestV5({
    schema: "loom.authoritative-rebuild-request/v5", requestId: "asset-definition-binding", sourceDefinition,
    evaluatorId: "loom.native-aircraft-occurrence-transition-union/v1", topology: "ordered-aircraft-occurrence-transition-union",
    definition: item.definition,
  }).definition;
  return { ...item, definitionRevision: sha(item.definitionRevision, "definitionRevision"),
    assemblyTopologyRevision: sha(item.assemblyTopologyRevision, "assemblyTopologyRevision"),
    poseRevision: sha(item.poseRevision, "poseRevision"), definition: parsed };
}

function descriptors(value: unknown, contacts: readonly string[], surface: LoomAircraftTransitionAssetHeader["surface"]): Descriptor[] {
  const specs = [
    ["surface.positions", "f64-le", "world-position-m", [surface.vertexCount, 3]],
    ["surface.faces", "u32-le", "triangle-vertex-indices", [surface.faceCount, 3]],
    ["surface.face_occurrence_indices", "u32-le", "per-face-occurrence-table-index-or-u32-max", [surface.faceCount]],
    ["surface.face_body_indices", "u32-le", "per-face-body-table-index-or-u32-max", [surface.faceCount]],
    ["surface.face_transition_body_indices", "u32-le", "per-face-transition-body-table-index-or-u32-max", [surface.faceCount]],
    ["surface.face_union_group_indices", "u32-le", "per-face-union-group-table-index", [surface.faceCount]],
    ["surface.face_patch_indices", "u32-le", "per-face-patch-table-index", [surface.faceCount]],
    ["surface.face_corner_parameters", "f64-le", "face-local-barycentric", [surface.faceCount, 3, 2]],
    ...contacts.map((name) => [name, "u32-le", "closed-transition-contact-vertex-indices", null]),
  ] as const;
  let offset = 0;
  return rows(value, "arrays", specs.length).map((value, index): Descriptor => {
    const item = exact(value, ["name", "dtype", "shape", "byteOffset", "byteLength", "semantic"], `arrays[${index}]`);
    const expected = specs[index]!; const shape = rows(item.shape, "array.shape").map((entry) => integer(entry, "array.shape"));
    if (item.name !== expected[0] || item.dtype !== expected[1] || item.semantic !== expected[2]
        || (expected[3] && canonical(shape) !== canonical(expected[3]))) throw new Error("transition array ABI is unsupported");
    const width = item.dtype === "f64-le" ? 8 : 4; const byteLength = integer(item.byteLength, "byteLength");
    const byteOffset = integer(item.byteOffset, "byteOffset");
    if (byteOffset !== offset || byteLength !== shape.reduce((product, extent) => product * extent, 1) * width) {
      throw new Error("transition array layout is invalid");
    }
    offset += byteLength;
    return { name: String(item.name), dtype: item.dtype as Descriptor["dtype"], shape,
      byteOffset, byteLength, semantic: String(item.semantic) };
  });
}

function parsedHeader(value: unknown): LoomAircraftTransitionAssetHeader {
  const root = exact(value, ["schema", "version", "acceptedSchema", "coordinateSystem", "definitionBinding",
    "cacheReceipt", "definitions", "occurrences", "bodies", "transitions", "transitionBodies",
    "unionGroups", "surface", "arrays", "payloadSha256", "capabilities"], "transition asset header");
  const v3 = root.schema === LOOM_TRANSITION_ASSET_SCHEMA_V3 && root.version === 3
    && root.acceptedSchema === LOOM_TRANSITION_ACCEPTED_SCHEMA_V3;
  const v2 = root.schema === LOOM_TRANSITION_ASSET_SCHEMA && root.version === 2
    && root.acceptedSchema === LOOM_TRANSITION_ACCEPTED_SCHEMA;
  if ((!v2 && !v3)
      || canonical(root.coordinateSystem) !== canonical({ axes: "x-aft-y-starboard-z-up", handedness: "right", lengthUnit: "m" })) {
    throw new Error("transition asset identity is unsupported");
  }
  const binding = definitionBinding(root.definitionBinding); const definition = binding.definition;
  const occurrenceRows = rows(root.occurrences, "occurrences", 3).map((value, index) => {
    const row = object(value, `occurrences[${index}]`);
    if (integer(row.occurrenceIndex, "occurrenceIndex") !== index || !["wing", "fuselage"].includes(String(row.role))
        || !["proper", "reflected"].includes(String(row.parity))) throw new Error("occurrence row is invalid");
    return { ...row, occurrenceIndex: index, occurrenceId: string(row.occurrenceId, "occurrenceId"),
      bodyIndex: integer(row.bodyIndex, "bodyIndex"), bodyId: string(row.bodyId, "bodyId"),
      unionGroupIndex: integer(row.unionGroupIndex, "unionGroupIndex"), role: row.role as "wing" | "fuselage",
      parity: row.parity as "proper" | "reflected" };
  });
  const bodyRows = rows(root.bodies, "bodies", 3).map((value, index) => {
    const row = object(value, `bodies[${index}]`);
    if (integer(row.bodyIndex, "bodyIndex") !== index) throw new Error("body row is invalid");
    return { ...row, bodyIndex: index, bodyId: string(row.bodyId, "bodyId"),
      occurrenceIndex: integer(row.occurrenceIndex, "occurrenceIndex"), occurrenceId: string(row.occurrenceId, "occurrenceId") };
  });
  const transitionRows = rows(root.transitions, "transitions", 1).map((value) => {
    const row = object(value, "transitions[0]");
    return { ...row, transitionIndex: integer(row.transitionIndex, "transitionIndex"),
      transitionId: string(row.transitionId, "transitionId"), definition: row.definition,
      ...(v3 ? { continuityReceipt: parseLoomJunctionContinuityReceiptV3(row.continuityReceipt) } : {}) };
  });
  const transitionBodyRows = rows(root.transitionBodies, "transitionBodies", 2).map((value, index) => {
    const row = object(value, `transitionBodies[${index}]`); const side = row.side;
    if (integer(row.transitionBodyIndex, "transitionBodyIndex") !== index || (side !== "starboard" && side !== "port")) throw new Error("transition body row is invalid");
    return { ...row, transitionBodyIndex: index, transitionId: string(row.transitionId, "transitionId"),
      bodyId: string(row.bodyId, "bodyId"), side: side as "starboard" | "port" };
  });
  const groupRows = rows(root.unionGroups, "unionGroups", 1).map((value) => {
    const row = object(value, "unionGroups[0]"); const patches = rows(row.patches, "patches", 5).map((value, index) => {
      const patch = object(value, `patches[${index}]`); const ownerKind = patch.ownerKind;
      if (integer(patch.patchIndex, "patchIndex") !== index || (ownerKind !== "occurrence" && ownerKind !== "transition")) throw new Error("transition patch row is invalid");
      return { ...patch, patchIndex: index, ownerKind: ownerKind as "occurrence" | "transition",
        ownerIndex: integer(patch.ownerIndex, "ownerIndex") };
    });
    const weldedContacts = rows(row.weldedContacts, "weldedContacts", 4).map((value, index) => {
      const contact = object(value, `weldedContacts[${index}]`);
      if (integer(contact.contactIndex, "contactIndex") !== index || contact.closed !== true || contact.maximumGapM !== 0) throw new Error("transition contact is not exact zero-gap");
      return { ...contact, contactIndex: index, vertexArrayName: string(contact.vertexArrayName, "vertexArrayName") };
    });
    return { ...row, unionGroupIndex: integer(row.unionGroupIndex, "unionGroupIndex"),
      unionGroupId: string(row.unionGroupId, "unionGroupId"), geometryRevision: sha(row.geometryRevision, "geometryRevision"),
      topologyRevision: sha(row.topologyRevision, "topologyRevision"), patches, weldedContacts };
  });
  const rawSurface = object(root.surface, "surface");
  const surface = { ...rawSurface,
    representation: rawSurface.representation as LoomAircraftTransitionAssetHeader["surface"]["representation"],
    geometryRevision: sha(rawSurface.geometryRevision, "geometryRevision"), topologyRevision: sha(rawSurface.topologyRevision, "topologyRevision"),
    vertexCount: integer(rawSurface.vertexCount, "vertexCount"), faceCount: integer(rawSurface.faceCount, "faceCount"),
    patchCount: integer(rawSurface.patchCount, "patchCount"), unionGroupCount: integer(rawSurface.unionGroupCount, "unionGroupCount"),
    transitionCount: integer(rawSurface.transitionCount, "transitionCount"), transitionBodyCount: integer(rawSurface.transitionBodyCount, "transitionBodyCount"),
    qc: object(rawSurface.qc, "surface.qc") };
  const expectedRepresentation = v3 ? "adapted-parametric-explicit-transition-continuity-union" : "adapted-parametric-explicit-transition-union";
  if (surface.representation !== expectedRepresentation || surface.unionGroupCount !== 1
      || surface.transitionCount !== 1 || surface.transitionBodyCount !== 2 || surface.patchCount !== 5
      || surface.qc.canonicalQcPassed !== true || surface.qc.boundaryEdgeCount !== 0
      || surface.qc.nonmanifoldEdgeCount !== 0 || surface.qc.selfIntersectionCount !== 0) throw new Error("transition surface did not pass canonical QC");
  const capabilities = object(root.capabilities, "capabilities");
  const expectedCapabilities = { assemblyClass: "symmetric-y-star-wing-fuselage-transition/v1",
    representation: expectedRepresentation, junctionTransitions: true,
    ...(v3 ? { junctionContinuity: { silentDowngrade: false,
      wing: { maximumContinuity: "g1-tangent", g2Available: false, adapterId: "analytic-ruled-wing-directional-g1/v1" },
      fuselage: { maximumContinuity: "g2-curvature", g2Available: true, adapterId: "analytic-pchip-superellipse-directional-g2/v1" } } } : {}),
    verticalTailDorsalAdapter: false, arbitraryProperRigidOccurrences: false, explicitReflectedOccurrences: true,
    genericNaryUnion: false, sourceSurfaceParameters: false, authoritativeAeroPanels: false,
    authoritativeWake: false, authoritativeJacobian: false, exactSourceSurface: false };
  if (canonical(capabilities) !== canonical(expectedCapabilities)) throw new Error("transition asset capabilities overstate support");
  const cache = object(root.cacheReceipt, "cacheReceipt");
  const contactNames = groupRows[0]!.weldedContacts.map(({ vertexArrayName }) => vertexArrayName);
  const arrayRows = descriptors(root.arrays, contactNames, surface);
  if (canonical(transitionRows[0]?.definition) !== canonical(definition.transitions[0])
      || transitionRows[0]?.transitionId !== definition.transitions[0]?.transitionId
      || surface.geometryRevision !== groupRows[0]?.geometryRevision
      || surface.topologyRevision !== groupRows[0]?.topologyRevision) throw new Error("transition tables disagree with their definition");
  return { ...root, schema: v3 ? LOOM_TRANSITION_ASSET_SCHEMA_V3 : LOOM_TRANSITION_ASSET_SCHEMA,
    version: v3 ? 3 : 2, acceptedSchema: v3 ? LOOM_TRANSITION_ACCEPTED_SCHEMA_V3 : LOOM_TRANSITION_ACCEPTED_SCHEMA,
    definitionBinding: binding, cacheReceipt: { ...cache, sceneRevision: sha(cache.sceneRevision, "sceneRevision") },
    occurrences: occurrenceRows, bodies: bodyRows, transitions: transitionRows, transitionBodies: transitionBodyRows,
    unionGroups: groupRows, surface, arrays: arrayRows, payloadSha256: sha(root.payloadSha256, "payloadSha256"),
    capabilities: capabilities as LoomAircraftTransitionAssetHeader["capabilities"] };
}

export async function verifyLoomAircraftTransitionAssetBuffer(buffer: ArrayBuffer): Promise<LoomVerifiedAircraftTransitionAsset> {
  if (buffer.byteLength < PREFIX_BYTES || buffer.byteLength > MAX_ASSET_BYTES) throw new Error("transition asset extent is invalid");
  const magic = new Uint8Array(buffer, 0, 8);
  const prefix = new DataView(buffer); const version = prefix.getUint32(8, true); const headerLength = prefix.getUint32(12, true);
  const expectedMagic = version === 3 ? MAGIC_V3 : MAGIC_V2;
  if (!magic.every((byte, index) => byte === expectedMagic[index])) throw new Error("transition asset magic is invalid");
  const payloadOffset = PREFIX_BYTES + ((headerLength + 3) & ~3);
  if ((version !== 2 && version !== 3) || headerLength < 2 || headerLength > MAX_HEADER_BYTES || payloadOffset >= buffer.byteLength) throw new Error("transition asset prefix is invalid");
  const headerText = new TextDecoder().decode(new Uint8Array(buffer, PREFIX_BYTES, headerLength));
  const header = parsedHeader(JSON.parse(headerText));
  if (canonical(JSON.parse(headerText)) !== headerText) throw new Error("transition asset header is not canonical JSON");
  const payload = buffer.slice(payloadOffset); const digest = await crypto.subtle.digest("SHA-256", payload);
  const payloadSha = `sha256:${[...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("")}`;
  if (payloadSha !== header.payloadSha256) throw new Error("transition asset payload SHA-256 mismatch");
  const descriptor = (name: string) => header.arrays.find((row) => row.name === name)!;
  const f64 = (name: string) => { const row = descriptor(name); const start = payloadOffset + row.byteOffset;
    if (start % 8 === 0) return new Float64Array(buffer, start, row.byteLength / 8);
    const result = new Float64Array(row.byteLength / 8); const view = new DataView(buffer, start, row.byteLength);
    result.forEach((_value, index) => { result[index] = view.getFloat64(index * 8, true); }); return result; };
  const u32 = (name: string) => { const row = descriptor(name); return new Uint32Array(buffer, payloadOffset + row.byteOffset, row.byteLength / 4); };
  const positions = f64("surface.positions"); const faces = u32("surface.faces");
  const occurrences = u32("surface.face_occurrence_indices"); const bodies = u32("surface.face_body_indices");
  const transitionBodies = u32("surface.face_transition_body_indices"); const groups = u32("surface.face_union_group_indices");
  const patches = u32("surface.face_patch_indices"); const faceEntityIds: string[] = [];
  for (let face = 0; face < header.surface.faceCount; face += 1) {
    const vertices = [faces[face * 3]!, faces[face * 3 + 1]!, faces[face * 3 + 2]!];
    const occurrenceOwned = occurrences[face] !== UINT32_NONE; const transitionOwned = transitionBodies[face] !== UINT32_NONE;
    const patch = header.unionGroups[0]!.patches[patches[face]!];
    if (new Set(vertices).size !== 3 || vertices.some((vertex) => vertex >= header.surface.vertexCount)
        || occurrenceOwned === transitionOwned || groups[face] !== 0 || !patch) throw new Error("transition face ownership is invalid");
    if (occurrenceOwned) {
      const owner = header.occurrences[occurrences[face]!];
      if (!owner || bodies[face] !== owner.bodyIndex || patch.ownerKind !== "occurrence"
          || patch.ownerIndex !== owner.occurrenceIndex) throw new Error("transition occurrence ownership is forged");
      faceEntityIds.push(owner.occurrenceId);
    } else {
      const owner = header.transitionBodies[transitionBodies[face]!];
      if (!owner || bodies[face] !== UINT32_NONE || patch.ownerKind !== "transition"
          || patch.ownerIndex !== owner.transitionBodyIndex) throw new Error("transition body ownership is forged");
      faceEntityIds.push(owner.bodyId);
    }
  }
  header.unionGroups[0]!.weldedContacts.forEach(({ vertexArrayName }) => {
    const vertices = u32(vertexArrayName);
    if (vertices.length < 3 || new Set(vertices).size !== vertices.length
        || vertices.some((vertex) => vertex >= header.surface.vertexCount)) throw new Error("transition contact vertices are invalid");
  });
  const displayPositions = Float32Array.from(positions);
  if (!displayPositions.every(Number.isFinite)) throw new Error("transition asset cannot be displayed safely");
  const entityKinds = Object.fromEntries([
    ...header.occurrences.map(({ occurrenceId }) => [occurrenceId, "occurrence"] as const),
    ...header.transitionBodies.map(({ bodyId }) => [bodyId, "transition"] as const),
  ]);
  return { integrity: { status: "verified", payloadSha256: true }, header, positions, faces,
    faceOccurrenceIndices: occurrences, faceBodyIndices: bodies, faceTransitionBodyIndices: transitionBodies,
    facePatchIndices: patches, displaySurface: { entityKinds, faceEntityIds, faces,
      geometrySource: "retained-display-faces", positions: displayPositions } };
}
